import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { isAuthenticationEnabled } from '~/lib/auth';
import AppConfig from '~/utils/appconfig';
import ConnectorAccessGuard from './ConnectorAccessGuard';

interface AuthenticationGuardProps {
    children: React.ReactNode;
}

/**
 * Establishes who the user is before a protected route opens.
 *
 * Which question that is depends on what is serving the application. Against
 * the cloud backend it is whether they have signed in, through whichever
 * identity provider is configured, and the answer is a redirect to the sign-in
 * page. Against a connector the host has already settled it over the network,
 * and what remains is what the connector says about them — which is a
 * different thing to ask and a different thing to say.
 */
const AuthenticationGuard: React.FC<AuthenticationGuardProps> = ({
    children,
}) => {
    const location = useLocation();
    const { isAuthenticated } = useAuthSession();
    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();

    // A connector settles identity at the HTTP layer rather than through a
    // sign-in this application performs, so there is nothing to redirect to —
    // but there is still an answer to read.
    if (AppConfig.isConnectorMode) {
        return <ConnectorAccessGuard>{children}</ConnectorAccessGuard>;
    }

    // Allow access if authentication is disabled or if in connector route
    if (!isAuthenticationEnabled() || isInConnectorRoute) {
        return <>{children}</>;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // User is authenticated, render the protected content
    return <>{children}</>;
};

export default AuthenticationGuard;
